import { useQuery, useQueryClient, useMutation, useInfiniteQuery } from '@tanstack/react-query';
import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { pushApi, tradersApi, billingApi, ApiError } from '~/lib/api';
import type { TraderAlert } from '~/lib/api';
import { usePushNotifications } from '~/hooks/usePushNotifications';
import { ErrorBoundary } from '~/components/ErrorBoundary';
import { InlineError } from '~/components/ErrorPage';

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// Three-state notification mode collapsed from the (notify_plays, notify_messages)
// flag pair. Same semantics as the trader detail page picker; legacy (0, 1)
// surfaces as 'plays_and_messages'.
type NotifyMode = 'silent' | 'plays' | 'plays_and_messages';

function modeFromTrader(t: TraderAlert): NotifyMode {
  if (!t.followed) return 'silent';
  if (t.notify_messages) return 'plays_and_messages';
  if (t.notify_plays) return 'plays';
  return 'silent';
}

function flagsFromMode(mode: NotifyMode) {
  return {
    notify_plays: mode === 'plays' || mode === 'plays_and_messages',
    notify_messages: mode === 'plays_and_messages',
  };
}

// 'silent' isn't a pill — it's the default when none of the others are
// selected. Clicking the active pill again toggles back to silent.
const ROW_NOTIFY_OPTIONS: Array<{ mode: 'plays' | 'plays_and_messages'; label: string }> = [
  { mode: 'plays', label: 'Plays' },
  { mode: 'plays_and_messages', label: 'Plays + Msgs' },
];

// Top-level filter for the trader list. Toggles are independent — selecting
// 'silent' AND 'plays' shows traders that are in either mode. No selection
// means everyone is visible.
const MODE_FILTER_OPTIONS: Array<{ mode: NotifyMode; label: string }> = [
  { mode: 'silent', label: 'Silent' },
  { mode: 'plays', label: 'Plays' },
  { mode: 'plays_and_messages', label: 'Plays + Msgs' },
];

function ModeFilterPills({
  filter,
  onChange,
}: {
  filter: Set<NotifyMode>;
  onChange: (next: Set<NotifyMode>) => void;
}) {
  const toggle = (m: NotifyMode) => {
    const next = new Set(filter);
    if (next.has(m)) next.delete(m);
    else next.add(m);
    onChange(next);
  };
  return (
    <div className="flex flex-wrap gap-1">
      {MODE_FILTER_OPTIONS.map(({ mode, label }) => {
        const active = filter.has(mode);
        return (
          <button
            key={mode}
            type="button"
            onClick={() => toggle(mode)}
            className={clsx(
              'px-3 py-1.5 rounded text-xs font-mono font-medium transition-colors',
              active
                ? 'bg-accent text-surface-0'
                : 'bg-surface-2 text-text-secondary hover:bg-surface-3 hover:text-text-primary',
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function TraderRow({
  trader,
  subscribed,
  onSetNotify,
  disabled,
}: {
  trader: TraderAlert;
  subscribed: boolean;
  onSetNotify: (mode: NotifyMode) => void;
  disabled: boolean;
}) {
  const current = modeFromTrader(trader);
  return (
    <div className="flex items-center justify-between px-4 py-3 gap-3 flex-wrap">
      <Link
        to={`/traders/${trader.trader_id}`}
        className="min-w-0 flex-1 hover:text-accent transition-colors group"
      >
        <p className="text-sm font-medium text-text-primary truncate group-hover:text-accent">
          {trader.trader_display_name || trader.current_nick || trader.trader_username}
        </p>
        {trader.trader_display_name && (
          <p className="text-xs text-text-muted truncate">{trader.current_nick || trader.trader_username}</p>
        )}
      </Link>

      {subscribed && (
        <div className="flex flex-wrap gap-1 shrink-0" role="group" aria-label={`Notifications for ${trader.trader_display_name || trader.trader_username}`}>
          {ROW_NOTIFY_OPTIONS.map(({ mode, label }) => {
            const active = current === mode;
            // Click active again → back to silent (no pill highlighted).
            const next: NotifyMode = active ? 'silent' : mode;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => onSetNotify(next)}
                disabled={disabled}
                className={clsx(
                  'px-2.5 py-1 rounded text-[11px] font-mono font-medium transition-colors disabled:opacity-50',
                  active
                    ? 'bg-accent text-surface-0'
                    : 'bg-surface-2 text-text-secondary hover:bg-surface-3 hover:text-text-primary',
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Top traders strip — three cards each with an oversized faded rank number
// as a backdrop, trader name on top, follower count as a small subtitle,
// and an inline Follow / Following toggle so newcomers can opt in to
// alerts without leaving the page. Self-hides when no follows exist yet.
function TopTradersCard() {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['traders', 'top', 3],
    queryFn: () => tradersApi.top(3),
    staleTime: 5 * 60_000,
  });

  // Optimistic follow/unfollow. Setting notify_plays=true creates the
  // follow row with the default Plays mode; clearing it deletes the row.
  // The mutationFn return value is intentionally untyped — we don't
  // consume it, we just refetch on settle.
  const followMutation = useMutation<unknown, Error, { traderId: string; follow: boolean }, { prev: unknown }>({
    mutationFn: async ({ traderId, follow }) => {
      if (follow) {
        await tradersApi.updateNotifications(traderId, { notify_plays: true });
      } else {
        await tradersApi.unfollow(traderId);
      }
    },
    onMutate: async ({ traderId, follow }) => {
      const key = ['traders', 'top', 3];
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData(key);
      queryClient.setQueryData(key, (old: typeof data) =>
        old
          ? {
              ...old,
              traders: old.traders.map((t) =>
                t.trader_id === traderId
                  ? { ...t, followed: follow, follower_count: t.follower_count + (follow ? 1 : -1) }
                  : t
              ),
            }
          : old
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev !== undefined) {
        queryClient.setQueryData(['traders', 'top', 3], ctx.prev);
      }
    },
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: ['traders', 'top'] });
      queryClient.invalidateQueries({ queryKey: ['push', 'traders'] });
      queryClient.invalidateQueries({ queryKey: ['trader-profile', vars.traderId] });
    },
  });

  const top = data?.traders ?? [];
  if (top.length === 0) return null;

  // #1 gets a faint accent treatment so it stands apart from #2/#3.
  const rankAccent = ['text-accent/15', 'text-text-muted/15', 'text-text-muted/10'];

  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-3">
      {top.map((t, i) => (
        <Link
          key={t.trader_id}
          to={`/traders/${t.trader_id}`}
          className="card relative overflow-hidden p-3 sm:p-4 hover:bg-surface-2 transition-colors min-w-0"
        >
          {/* Backdrop rank glyph — purely decorative, sits behind the text */}
          <span
            aria-hidden="true"
            className={clsx(
              'absolute -right-2 -bottom-4 sm:-right-3 sm:-bottom-6 select-none pointer-events-none',
              'font-bold leading-none text-6xl sm:text-7xl',
              rankAccent[i] ?? 'text-text-muted/10',
            )}
          >
            {i + 1}
          </span>

          <div className="relative flex flex-col h-full">
            <p className="text-xs font-mono uppercase tracking-wider text-text-muted">
              #{i + 1}
            </p>
            <p className="text-sm sm:text-base font-medium text-text-primary truncate mt-1">
              {t.current_nick || t.trader_username}
            </p>
            <p className="text-[10px] sm:text-xs text-text-muted mt-1">
              {t.follower_count} follower{t.follower_count !== 1 ? 's' : ''}
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                followMutation.mutate({ traderId: t.trader_id, follow: !t.followed });
              }}
              disabled={followMutation.isPending}
              className={clsx(
                'mt-3 px-2.5 py-1 rounded text-[11px] font-mono font-medium transition-colors disabled:opacity-50 self-start',
                t.followed
                  ? 'bg-accent/15 text-accent ring-1 ring-accent/40'
                  : 'bg-accent text-surface-0 hover:bg-accent/90',
              )}
            >
              {t.followed ? 'Following' : 'Follow'}
            </button>
          </div>
        </Link>
      ))}
    </div>
  );
}

function TradersPageContent() {
  const queryClient = useQueryClient();
  const push = usePushNotifications();

  const { data: billingData } = useQuery({
    queryKey: ['billing'],
    queryFn: billingApi.status,
  });
  const hasActiveSubscription = billingData?.isActive ?? false;

  const checkoutMutation = useMutation({
    mutationFn: billingApi.createCheckout,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  const [searchInput, setSearchInput] = useState('');
  // Multi-select mode filter — empty set = show all. Each pill toggles
  // independently; the visible list is the union of all matching modes.
  const [modeFilter, setModeFilter] = useState<Set<NotifyMode>>(new Set());
  const pageSize = 50;

  const debouncedSearch = useDebounce(searchInput, 300);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
  }, []);

  // Infinite scroll: each page fetches `pageSize` traders; getNextPageParam
  // returns the next offset until we've loaded everything the API knows about.
  // The traders list page key intentionally omits modeFilter — the filter is
  // client-side over the cumulative array, so we don't refetch when toggling.
  const queryKey = ['push', 'traders', debouncedSearch, pageSize];
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      pushApi.traders({
        limit: pageSize,
        offset: pageParam,
        search: debouncedSearch || undefined,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.traders.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });

  const allTraders = data?.pages.flatMap((p) => p.traders) ?? [];
  // Apply the mode filter client-side. Empty selection = show everything.
  const traders = modeFilter.size === 0
    ? allTraders
    : allTraders.filter((t) => modeFilter.has(modeFromTrader(t)));

  // Sentinel observer — when the bottom-of-list spacer becomes visible
  // and we still have more pages on the server, kick off the next fetch.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Per-trader notification mode update via the new follow-flag system.
  // Optimistic — immediately reflect the new mode in the cached row, roll
  // back on error, then refetch to reconcile.
  const notifyMutation = useMutation({
    mutationFn: ({ traderId, mode }: { traderId: string; mode: NotifyMode }) =>
      tradersApi.updateNotifications(traderId, flagsFromMode(mode)),
    onMutate: async ({ traderId, mode }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      const flags = flagsFromMode(mode);
      // We always upsert a follow row, even for silent (both flags 0).
      const followed = true;
      queryClient.setQueryData(queryKey, (old: typeof data) =>
        old
          ? {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                traders: page.traders.map((t) =>
                  t.trader_id === traderId
                    ? { ...t, followed, notify_plays: flags.notify_plays, notify_messages: flags.notify_messages }
                    : t
                ),
              })),
            }
          : old
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: ['push', 'traders'] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['trader-profile', vars.traderId] });
    },
  });

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-text-primary">Traders</h1>
        <p className="text-sm sm:text-base text-text-secondary">
          Manage push notification alerts per trader
        </p>
      </div>

      <TopTradersCard />


      {hasActiveSubscription && !push.subscribed && (
        <div className="card p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-text-primary">Push Notifications</p>
              <p className="text-xs text-text-secondary mt-0.5">
                {!push.supported
                  ? 'Not supported in this browser'
                  : push.permission === 'denied'
                    ? 'Blocked — enable in browser settings'
                    : 'Enable to get alerts and use per-trader controls'}
              </p>
              {push.error && (
                <p className="text-xs text-loss mt-1">{push.error}</p>
              )}
            </div>
            <button
              onClick={push.subscribe}
              disabled={!push.supported || push.loading || push.permission === 'denied'}
              className="btn btn-primary text-xs sm:text-sm px-4 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {push.loading ? '...' : 'Enable'}
            </button>
          </div>
        </div>
      )}

      {/* Filters — search + mode pills on one line. Mode pills wrap to a
          new row only on very narrow viewports. */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search trader..."
            className="input w-full pr-8"
          />
          {searchInput && (
            <button
              onClick={() => handleSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
              aria-label="Clear search"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {hasActiveSubscription && (
          <ModeFilterPills filter={modeFilter} onChange={setModeFilter} />
        )}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
        </div>
      )}

      {error && (
        <InlineError
          error={error instanceof ApiError ? error : (error as Error)}
          onRetry={() => {
            queryClient.invalidateQueries({ queryKey: ['push', 'traders'] });
          }}
        />
      )}

      {!isLoading && traders.length === 0 && !error && (
        <div className="card p-8 text-center">
          <p className="text-text-secondary">
            {debouncedSearch
              ? 'No traders match your search.'
              : 'No monitored traders yet. Traders will appear here once signals are processed.'}
          </p>
        </div>
      )}

      {traders.length > 0 && hasActiveSubscription && (
        <>
          <div className="card divide-y divide-border/50">
            {traders.map((trader) => (
              <TraderRow
                key={trader.trader_id}
                trader={trader}
                subscribed={push.subscribed}
                onSetNotify={(mode) =>
                  notifyMutation.mutate({ traderId: trader.trader_id, mode })
                }
                disabled={notifyMutation.isPending}
              />
            ))}
          </div>

          {/* Infinite-scroll sentinel — triggers fetchNextPage when scrolled
              into view (see the IntersectionObserver effect above). */}
          {isFetchingNextPage && (
            <div className="flex items-center justify-center py-2 text-xs text-text-muted">
              loading…
            </div>
          )}
          <div ref={sentinelRef} className="h-px" aria-hidden="true" />
        </>
      )}

      {traders.length > 0 && !hasActiveSubscription && (
        <div className="card overflow-hidden">
          <div className="relative">
            <div className="pointer-events-none select-none opacity-30 blur-[1px] divide-y divide-border/50">
              {traders.map((trader) => (
                <TraderRow
                  key={trader.trader_id}
                  trader={trader}
                  subscribed={true}
                  onSetNotify={() => {}}
                  disabled={true}
                />
              ))}
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-transparent via-surface-0/60 to-surface-0/90">
              <div className="text-center space-y-3">
                <p className="text-sm text-text-secondary">
                  <span className="text-warning font-semibold">Per-trader alerts</span> are a premium feature
                </p>
                <button
                  onClick={() => checkoutMutation.mutate()}
                  disabled={checkoutMutation.isPending}
                  className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-300 hover:to-yellow-400 text-gray-900 font-bold rounded-lg transition-all shadow-lg shadow-yellow-500/25 hover:shadow-yellow-500/40 hover:scale-105 text-sm sm:text-base"
                >
                  {checkoutMutation.isPending ? 'Loading...' : 'Upgrade to Unlock'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TradersPage() {
  return (
    <ErrorBoundary>
      <TradersPageContent />
    </ErrorBoundary>
  );
}
